import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { SubHeader } from './ThreeRowsHeader';
import { Logo } from '../header/Logo';
import { TranslatedFontSize } from '../../../../../theming/fontSizeTranslator';
import { useTheme } from '../../../../../theming/useTheme';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { NavigationLink } from '../oldHeader/primaryHeader/PrimaryHeader';
import { Link } from '../../../../partials/link/Link';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { MenuData } from '../header/RowWithFixedNav';
import { AdditionalStyleSheet } from '../../../../components.utils';
import { HamburgerMenu } from './HamburgerMenu';
import { ActionsBar } from './ActionsBar';
import { componentConfigThemeKey as threeRowsHeaderConfigThemeKey, ThreeRowsHeaderThemeProps } from './ThreeRowsHeader.theme';
import { MegaMenu } from './megaMenu';
import { isCurrentPage } from './header.util';


interface SecondRowHeaderProps {
    subHeader: SubHeader;
    menu: MenuData | null;
    windowsDetails: {origin: string; path: string; innerWidth: number};
    showAccessibilityIcon?: boolean;
    feedName?: string;
}

const getSecondRowStyle = (secondLineBackgroundColor: string, secondLineTextColor: string) => {
  return StyleSheet.create({
    wrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0 0 0 16px',
      height: '48px',
      textTransform: 'uppercase',
      backgroundColor: secondLineBackgroundColor,
      boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.25)',
    },
    logoAndHamburgerContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    linksList: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      columnGap: '16px',
      listStyle: 'none',
      whiteSpace: 'nowrap',
      padding: '0 20px 0 0',
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
    link: {
      color: secondLineTextColor,
      height: '48px',
      display: 'flex',
      flexWrap: 'wrap',
      position: 'relative',
      alignItems: 'center',
      textDecoration: 'none',
      ':hover + span': {
        display: 'block',
      },
    },
  });
};

const getIndicatorDisplayStyle = (navigationLink: NavigationLink, secondLineSelectedIndicatorColor: string, isCurrent: boolean) => {
  return StyleSheet.create({
    indicator: {
      height: '3px',
      backgroundColor: secondLineSelectedIndicatorColor,
      width: '100%',
      bottom: '0',
      position: 'absolute',
      display: isCurrent ? 'block' : 'none',
    },
  }).indicator;
};

const createMenuStyle = (): AdditionalStyleSheet => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
    },
  }) as AdditionalStyleSheet;
};

export const SecondRowHeader: React.FunctionComponent<SecondRowHeaderProps> = props => {
  const { subHeader, menu, showAccessibilityIcon, feedName, windowsDetails } = props;
  const { logoForLargeScreen, links, megaMenu } = subHeader;
  const { fontSizesStyles }: { fontSizesStyles: TranslatedFontSize } = useTheme();
  const fontStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall: TYPOGRAPHY_SIZE.BIG, fontSizeMedium: TYPOGRAPHY_SIZE.BIG, fontSizeLarge: TYPOGRAPHY_SIZE.BIG });
  const {
    secondLineBackgroundColor,
    secondLineTextColor,
    secondLineSelectedIndicatorColor } = useTheme<ThreeRowsHeaderThemeProps>(threeRowsHeaderConfigThemeKey);
  const style = getSecondRowStyle(secondLineBackgroundColor, secondLineTextColor);

  return (
    <div className={css(style.wrapper, fontStyle)}>
      <div className={css(style.logoAndHamburgerContainer)}>
        {menu && <HamburgerMenu iconColor={secondLineTextColor} links={menu.links} additionalStyle={createMenuStyle()} />}
        { logoForLargeScreen && (
        <Logo
          {...logoForLargeScreen}
          logoHeightInLargeScreen={1.8}
          logoHeightInMediumScreen={1.8}
          logoHeightInSmallScreen={1.8}
        />
        )}
      </div>
      {links && (
      <ul className={css(style.linksList)}>
        {links.map((navigationLink: NavigationLink, index: number) => {
          const indicatorStyle = getIndicatorDisplayStyle(navigationLink, secondLineSelectedIndicatorColor, isCurrentPage(navigationLink, windowsDetails, feedName));
          return (
            <li key={index} className={css(style.link)}>
              <Link className={css(style.link, fontStyle)} href={navigationLink.link.href}>
                <span>{navigationLink.link.text}</span>
              </Link>
              <span className={css(indicatorStyle)} />
            </li>
          );
        })}
        {megaMenu && <MegaMenu moreIconColor={secondLineTextColor} megaMenu={megaMenu} />}
      </ul>
      )}
      <ActionsBar iconColor={secondLineTextColor} showAccessibilityIcon={showAccessibilityIcon} />
    </div>
  );
};
